import React, { useEffect, useState, useCallback, useMemo } from "react";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import {
  Newspaper, RefreshCw, ExternalLink, Check, CheckCheck, Inbox,
  Wrench, XSquare, Undo2, ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { useToast } from "../hooks/use-toast";
import { cn } from "../lib/utils";
import { ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, Flame, Sparkles } from "lucide-react";
import { fmtMDTShort } from "../lib/time";
import { detectKeywordsForItem, DEFAULT_WATCH_KEYWORDS } from "../lib/keywords";
import { useSettings } from "../context/SettingsContext";

const SOURCE_STYLE = {
  cisa: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30",
  msrc: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  nvd: "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30",
  cccs: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30",
};

const STATUS_META = {
  read:            { label: "Read",           tone: "bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300", icon: CheckCheck },
  actioned:        { label: "Actioned",       tone: "bg-sky-500/10 border-sky-500/30 text-sky-700 dark:text-sky-300",                 icon: Wrench },
  not_applicable:  { label: "Not applicable", tone: "bg-muted text-muted-foreground border-border",                                     icon: XSquare },
};

const TABS = [
  { key: "new",            label: "New",            icon: Inbox },
  { key: "read",           label: "Read",           icon: CheckCheck },
  { key: "actioned",       label: "Actioned",       icon: Wrench },
  { key: "not_applicable", label: "Not applicable", icon: XSquare },
];

function SourcePill({ item }) {
  const cls = SOURCE_STYLE[item.source_key] || "bg-muted text-foreground border-border";
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide", cls)}>
      {item.source}
      {item.region && <span className="opacity-70">· {item.region}</span>}
    </span>
  );
}

function StatusPill({ status, changedBy, changedAt }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  const Icon = meta.icon;
  const changed = changedAt ? fmtMDTShort(changedAt) : null;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border text-[10.5px] font-semibold px-2 py-0.5", meta.tone)} title={changedBy ? `${meta.label} by ${changedBy}${changed ? " at " + changed : ""}` : meta.label}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function formatDate(raw) {
  if (!raw) return "";
  return fmtMDTShort(raw);
}

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "ul", "ol", "li", "strong", "em", "b", "i", "u", "code", "pre",
    "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "a", "table", "thead",
    "tbody", "tr", "td", "th", "hr", "span", "div",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ADD_ATTR: ["target"],
};

function ItemCard({ item, onStatus, matches }) {
  const [showBody, setShowBody] = useState(false);
  const st = item.status || "new";
  const isNew = st === "new";
  const changed = item.status_changed_at ? fmtMDTShort(item.status_changed_at) : null;
  const safeHtml = useMemo(
    () => (item.body_html ? DOMPurify.sanitize(item.body_html, PURIFY_CONFIG) : ""),
    [item.body_html]
  );
  const isMatch = matches && matches.length > 0;
  return (
    <article
      className={cn(
        "rounded-xl border p-4 flex flex-col sm:flex-row sm:items-start gap-4 transition-colors",
        isMatch
          ? "border-orange-500/50 bg-orange-500/10 hover:border-orange-500/70 dark:bg-orange-500/[0.14]"
          : "border-border bg-card hover:border-primary/40",
      )}
      data-testid={`news-card-${item.id}`}
      data-match={isMatch ? "1" : "0"}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-2">
          <SourcePill item={item} />
          {item.published && (
            <span className="text-[11.5px] text-muted-foreground">{formatDate(item.published)}</span>
          )}
          {isMatch && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-orange-500/40 bg-orange-500/15 text-orange-800 dark:text-orange-200 text-[10.5px] font-semibold px-2 py-0.5"
              data-testid={`news-match-pill-${item.id}`}
              title={`Watch keywords matched: ${matches.join(", ")}`}
            >
              <Flame className="w-3 h-3" /> {matches.slice(0, 2).join(" · ")}{matches.length > 2 && ` +${matches.length - 2}`}
            </span>
          )}
          {(item.also_reported_by || []).length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 text-muted-foreground text-[10.5px] font-medium px-2 py-0.5" title={`Also reported by: ${item.also_reported_by.join(", ")}`}>
              +{item.also_reported_by.length} more source{item.also_reported_by.length === 1 ? "" : "s"}
            </span>
          )}
          {!isNew && <StatusPill status={st} changedBy={item.status_changed_by} changedAt={item.status_changed_at} />}
        </div>
        <h3 className="mt-1.5 text-[14.5px] font-semibold text-foreground leading-snug">
          {item.link ? (
            <a href={item.link} target="_blank" rel="noopener noreferrer" className="hover:text-primary">
              {item.title}
            </a>
          ) : item.title}
        </h3>
        {item.excerpt && !showBody && (
          <p className="mt-1 text-[12.5px] text-muted-foreground leading-relaxed line-clamp-3">{item.excerpt}</p>
        )}
        {showBody && safeHtml && (
          <div
            className="mt-2 rounded-lg border border-border bg-muted/30 p-3 text-[13px] leading-relaxed prose prose-sm max-w-none dark:prose-invert news-body-html"
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        )}
        {item.has_body && (
          <button
            type="button"
            onClick={() => setShowBody((v) => !v)}
            className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-primary hover:underline"
            data-testid={`news-toggle-body-${item.id}`}
          >
            {showBody ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {showBody ? "Hide details" : "Show details"}
          </button>
        )}
        {!isNew && item.status_changed_by && (
          <p className="mt-1.5 text-[11px] text-muted-foreground italic">
            Set to {STATUS_META[st]?.label || st} by <span className="font-medium not-italic">{item.status_changed_by}</span>
            {changed && <> at <span className="not-italic">{changed}</span></>}
          </p>
        )}
      </div>
      <div className="flex sm:flex-col items-stretch gap-1.5 sm:w-44 shrink-0">
        {item.link && (
          <a href={item.link} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-border text-[12px] font-medium hover:border-primary/40 hover:text-primary"
            data-testid={`news-open-${item.id}`}>
            <ExternalLink className="w-3.5 h-3.5" /> Open source
          </a>
        )}
        {isNew ? (
          <>
            <Button size="sm" className="h-8 gap-1.5" onClick={() => onStatus(item.id, "read")} data-testid={`news-mark-read-${item.id}`}>
              <Check className="w-3.5 h-3.5" /> Mark read
            </Button>
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => onStatus(item.id, "actioned")} data-testid={`news-mark-actioned-${item.id}`}>
              <Wrench className="w-3.5 h-3.5" /> Actioned
            </Button>
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => onStatus(item.id, "not_applicable")} data-testid={`news-mark-na-${item.id}`}>
              <XSquare className="w-3.5 h-3.5" /> Not applicable
            </Button>
          </>
        ) : (
          <>
            {st !== "actioned" && (
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => onStatus(item.id, "actioned")} data-testid={`news-mark-actioned-${item.id}`}>
                <Wrench className="w-3.5 h-3.5" /> Actioned
              </Button>
            )}
            {st !== "not_applicable" && (
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => onStatus(item.id, "not_applicable")} data-testid={`news-mark-na-${item.id}`}>
                <XSquare className="w-3.5 h-3.5" /> Not applicable
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-8 gap-1.5" onClick={() => onStatus(item.id, "new")} data-testid={`news-mark-new-${item.id}`}>
              <Undo2 className="w-3.5 h-3.5" /> Reset to New
            </Button>
          </>
        )}
      </div>
    </article>
  );
}

export default function IndustryNews() {
  const { toast } = useToast();
  const { settings } = useSettings();
  const watchlist = settings?.news_watch_keywords?.length
    ? settings.news_watch_keywords
    : DEFAULT_WATCH_KEYWORDS;
  const [tab, setTab] = useState("new");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ items: [], counts: {}, total_new: 0, total_read: 0 });
  // Per-user preferences. Server is the source of truth; localStorage caches the last
  // known values so the UI doesn't flash "By date" on every mount.
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("ucv:news:sort_mode") || "date");
  const [sourceOrder, setSourceOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ucv:news:source_order") || "[]"); }
    catch { return []; }
  });
  const [availableSources, setAvailableSources] = useState([]);

  const load = useCallback(async (state, sort, force = false) => {
    try {
      const { data } = await api.get("/industry-news", { params: { state, sort, force } });
      setData(data);
      if (Array.isArray(data.sources)) setAvailableSources(data.sources);
    } catch {
      toast({ title: "Failed to load industry news", variant: "destructive" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [toast]);

  useEffect(() => { load(tab, sortMode); }, [tab, sortMode, load]);

  // Sync latest preferences from the backend once on mount.
  useEffect(() => {
    let cancelled = false;
    api.get("/me/news-prefs").then(({ data }) => {
      if (cancelled) return;
      const mode = data?.sort_mode || "date";
      setSortMode(mode);
      localStorage.setItem("ucv:news:sort_mode", mode);
      const order = data?.source_order?.length ? data.source_order : (data?.resolved_order || []);
      if (order?.length) {
        setSourceOrder(order);
        localStorage.setItem("ucv:news:source_order", JSON.stringify(order));
      }
      if (Array.isArray(data?.available_sources)) setAvailableSources(data.available_sources);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const persistPrefs = useCallback(async (patch) => {
    if ("sort_mode" in patch) {
      setSortMode(patch.sort_mode);
      localStorage.setItem("ucv:news:sort_mode", patch.sort_mode);
    }
    if ("source_order" in patch) {
      setSourceOrder(patch.source_order);
      localStorage.setItem("ucv:news:source_order", JSON.stringify(patch.source_order));
    }
    try { await api.put("/me/news-prefs", patch); }
    catch { toast({ title: "Preferences didn't save server-side", variant: "destructive" }); }
  }, [toast]);

  const moveSource = (idx, dir) => {
    const current = sourceOrder.length ? [...sourceOrder] : availableSources.map((s) => s.key);
    const j = idx + dir;
    if (j < 0 || j >= current.length) return;
    [current[idx], current[j]] = [current[j], current[idx]];
    persistPrefs({ source_order: current });
  };

  const resetSourceOrder = () => persistPrefs({ source_order: [] });

  const refresh = async () => {
    setRefreshing(true);
    await load(tab, sortMode, true);
    toast({ title: "News refreshed" });
  };

  const setStatus = async (id, status) => {
    try {
      await api.post("/industry-news/set-status", { id, status });
      await load(tab);
    } catch {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  const counts = useMemo(() => data.counts || {}, [data]);

  // Keyword matching state — pre-compute matches for every item once per page load.
  const [matchesOnly, setMatchesOnly] = useState(() => localStorage.getItem("ucv:news:matches_only") === "1");
  const [keywordSearch, setKeywordSearch] = useState("");
  const [sortMatchesFirst, setSortMatchesFirst] = useState(() => localStorage.getItem("ucv:news:matches_first") !== "0");

  const itemsWithMatches = useMemo(() => {
    return (data.items || []).map((it) => ({
      item: it,
      // Server already computes matches from the current watchlist; fall back
      // to a client-side pass so the highlight keeps working if we ever hit
      // an older cached payload.
      matches: Array.isArray(it.matches) && it.matches.length
        ? it.matches
        : detectKeywordsForItem(it, watchlist),
    }));
  }, [data.items, watchlist]);

  const matchCount = useMemo(() => itemsWithMatches.filter((r) => r.matches.length > 0).length, [itemsWithMatches]);

  const displayItems = useMemo(() => {
    const needle = keywordSearch.trim().toLowerCase();
    let rows = itemsWithMatches;
    if (needle) {
      rows = rows.filter((r) => r.matches.some((m) => m.toLowerCase().includes(needle)));
    } else if (matchesOnly) {
      rows = rows.filter((r) => r.matches.length > 0);
    }
    if (sortMatchesFirst) {
      rows = [...rows].sort((a, b) => {
        if ((b.matches.length > 0) !== (a.matches.length > 0)) return b.matches.length - a.matches.length;
        // Preserve existing (server) order among peers.
        return 0;
      });
    }
    return rows;
  }, [itemsWithMatches, matchesOnly, keywordSearch, sortMatchesFirst]);

  const toggleMatchesOnly = () => {
    setMatchesOnly((v) => {
      const next = !v;
      localStorage.setItem("ucv:news:matches_only", next ? "1" : "0");
      return next;
    });
  };
  const toggleMatchesFirst = () => {
    setSortMatchesFirst((v) => {
      const next = !v;
      localStorage.setItem("ucv:news:matches_first", next ? "1" : "0");
      return next;
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-primary text-[12px] font-semibold uppercase tracking-wider"><Newspaper className="w-3.5 h-3.5" /> Industry News</div>
          <h1 className="text-[26px] font-semibold tracking-tight mt-1">Security & platform advisories</h1>
          <p className="text-[13px] text-muted-foreground">Live feed from CISA, Microsoft MSRC, NIST NVD and Canadian CCCS.</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="news-sort-menu">
                <ArrowUpDown className="w-3.5 h-3.5" /> Sort: {sortMode === "source" ? "By source" : "By date"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Sort mode</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => persistPrefs({ sort_mode: "date" })} data-testid="news-sort-date" className={cn(sortMode === "date" && "font-semibold text-primary")}>
                By date (newest first)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => persistPrefs({ sort_mode: "source" })} data-testid="news-sort-source" className={cn(sortMode === "source" && "font-semibold text-primary")}>
                By source (grouped)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center justify-between">
                <span>Source priority</span>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); resetSourceOrder(); }}
                  className="inline-flex items-center gap-1 text-[10.5px] normal-case tracking-normal text-muted-foreground hover:text-foreground"
                  data-testid="news-sort-reset"
                >
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              </DropdownMenuLabel>
              {(() => {
                const order = sourceOrder.length ? sourceOrder : availableSources.map((s) => s.key);
                const byKey = Object.fromEntries(availableSources.map((s) => [s.key, s]));
                return order.map((key, idx) => {
                  const meta = byKey[key];
                  if (!meta) return null;
                  return (
                    <div key={key} className="flex items-center justify-between gap-2 px-2 py-1" data-testid={`news-sort-order-${key}`}>
                      <div className="text-[12.5px]"><span className="font-semibold">{idx + 1}.</span> {meta.label} <span className="text-muted-foreground text-[10.5px]">· {meta.region}</span></div>
                      <div className="flex items-center gap-0.5">
                        <button
                          disabled={idx === 0}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveSource(idx, -1); }}
                          className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                          data-testid={`news-sort-up-${key}`}
                        ><ArrowUp className="w-3 h-3" /></button>
                        <button
                          disabled={idx === order.length - 1}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveSource(idx, +1); }}
                          className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                          data-testid={`news-sort-down-${key}`}
                        ><ArrowDown className="w-3 h-3" /></button>
                      </div>
                    </div>
                  );
                });
              })()}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} className="gap-1.5" data-testid="news-refresh-btn">
            <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 max-w-2xl">
          {TABS.map((t) => {
            const Icon = t.icon;
            const n = counts[t.key] ?? 0;
            return (
              <TabsTrigger key={t.key} value={t.key} className="gap-2" data-testid={`news-tab-${t.key}`}>
                <Icon className="w-4 h-4" /> {t.label}
                <span className={cn(
                  "ml-1 inline-flex items-center rounded-full text-[11px] font-semibold px-1.5 py-0.5",
                  t.key === "new" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                )}>
                  {n}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Keyword controls — match-only toggle, watchlist search, sort-matches-first. */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={toggleMatchesOnly}
            data-testid="news-matches-only-toggle"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium transition-colors",
              matchesOnly
                ? "border-orange-500/50 bg-orange-500/15 text-orange-800 dark:text-orange-200"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
            title={`Show only advisories that mention a watched product (${matchCount} match${matchCount === 1 ? "" : "es"} on this tab)`}
          >
            <Flame className="w-3.5 h-3.5" />
            Matches only <span className="opacity-75">({matchCount})</span>
          </button>
          <button
            type="button"
            onClick={toggleMatchesFirst}
            data-testid="news-matches-first-toggle"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium transition-colors",
              sortMatchesFirst ? "border-primary/50 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
            )}
            title="Sort matched advisories to the top of the list"
          >
            <Sparkles className="w-3.5 h-3.5" /> Matches first
          </button>
          <div className="relative">
            <input
              type="text"
              value={keywordSearch}
              onChange={(e) => setKeywordSearch(e.target.value)}
              placeholder="Filter by keyword (e.g. Microsoft, ThreatLocker, VMware)"
              list="ucv-watch-keywords"
              className="h-8 min-w-[280px] rounded-md border border-border bg-background px-3 text-[12.5px] outline-none focus:border-primary/50"
              data-testid="news-keyword-search"
            />
            <datalist id="ucv-watch-keywords">
              {watchlist.map((w) => <option key={w} value={w} />)}
            </datalist>
          </div>
          {(keywordSearch || matchesOnly) && (
            <button
              type="button"
              onClick={() => { setKeywordSearch(""); setMatchesOnly(false); localStorage.setItem("ucv:news:matches_only", "0"); }}
              className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground hover:text-foreground"
              data-testid="news-keyword-clear"
            >
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            <List
              loading={loading}
              rows={displayItems}
              onStatus={setStatus}
              emptyLabel={
                t.key === "new" ? "You're all caught up — no new advisories." :
                t.key === "read" ? "Nothing marked as read yet." :
                t.key === "actioned" ? "No advisories marked as actioned yet." :
                "No advisories marked as not applicable yet."
              }
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function List({ loading, rows, onStatus, emptyLabel }) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/30 py-14 text-center">
        <Inbox className="w-8 h-8 mx-auto text-muted-foreground" />
        <p className="mt-2 text-[13.5px] text-muted-foreground">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {rows.map(({ item, matches }) => (
        <ItemCard key={item.id} item={item} matches={matches} onStatus={onStatus} />
      ))}
    </div>
  );
}
